import { Link } from 'react-router-dom';

const Home = () => {
  return (
    <>
      {/* ---------- Hero ---------- */}
      <section className="hero">
        <div className="hero-bg">
          <img src="./images/exterior/fallpic.jpg" alt="The log cabin in autumn foliage" />
        </div>
        <div className="container hero-content">
          <span className="eyebrow reveal d1">501(c)(3) Nonprofit Retreat · Bristol, NH</span>
          <h1 className="reveal d2">A log cabin retreat in the White Mountains.</h1>
          <p className="reveal d3">
            Seven wooded acres. Room for twenty-seven. A nonprofit retreat serving youth
            groups, churches and charitable organizations in New Hampshire's Lakes Region.
          </p>
          <div className="hero-actions reveal d4">
            <Link to="/facilities" className="btn btn--primary">
              Tour the cabin <span className="arrow">→</span>
            </Link>
            <Link to="/contact" className="btn btn--ghost">Plan your stay</Link>
          </div>
        </div>
        <div className="scroll-cue"><span /></div>
      </section>

      {/* ---------- Intro ---------- */}
      <section className="section intro">
        <div className="container intro-grid">
          <div className="intro-lead">
            <span className="eyebrow">Welcome</span>
            <p style={{ marginTop: '1.2rem' }}>
              Nestled on seven acres of land in the lovely New England town of Bristol, NH —
              just one mile from Newfound Lake — Upward Bound Youth Development, Inc. makes its
              home. A <strong>501(c)(3) nonprofit</strong>, Upward Bound exists first to serve
              youth groups, then church groups and other charitable organizations, with families
              welcomed as availability allows. Our spacious log cabin accommodates up to
              twenty-seven people comfortably.
            </p>
            <p>
              There is plenty of room to spread out when you need space, or to gather in the
              large family room for a meeting or to watch television — whatever your needs, our
              cabin will serve them all. The large kitchen is fully equipped, or you may wish to
              visit one of the many fine restaurants in the area.
            </p>
            <p>
              Come visit this beautiful small New England town in the Lakes Region of central
              New Hampshire. Located at the foot of the White Mountains, this area has so much
              to offer for the young and the old alike.
            </p>
          </div>

          <aside className="intro-aside">
            <h3>The cabin at a glance</h3>
            <ul>
              <li><b>27</b><span>Sleeps up to twenty-seven guests</span></li>
              <li><b>4</b><span>Four bedrooms plus a basement apartment</span></li>
              <li><b>7</b><span>Seven private wooded acres</span></li>
              <li><b>1mi</b><span>One mile to Newfound Lake</span></li>
            </ul>
          </aside>
        </div>
      </section>

      {/* ---------- Who the cabin is for ---------- */}
      <section className="section serve">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Who the cabin is for</span>
            <h2>A nonprofit retreat, with purpose.</h2>
            <p>As a 501(c)(3), Upward Bound offers the cabin on a priority basis.</p>
          </div>
          <div className="serve-grid">
            <article className="serve-tier">
              <div className="rank">1</div>
              <h3>Youth groups</h3>
              <p>Our first and foremost purpose — creating space for young people to gather, learn and grow.</p>
            </article>
            <article className="serve-tier">
              <div className="rank">2</div>
              <h3>Churches &amp; charitable organizations</h3>
              <p>Mission-aligned groups looking for a place to retreat, meet and serve.</p>
            </article>
            <article className="serve-tier">
              <div className="rank">3</div>
              <h3>Families</h3>
              <p>Welcomed as availability allows, once priority groups have been served.</p>
            </article>
          </div>
        </div>
      </section>

      {/* ---------- Seasons: Summer ---------- */}
      <section className="section seasons">
        <div className="container">
          <div className="feature">
            <div className="feature-media reveal d1">
              <img src="./images/exterior/fallpic.jpg" alt="Autumn at the cabin" />
              <span className="tag">Spring · Summer · Fall</span>
            </div>
            <div className="feature-text">
              <span className="eyebrow">The warm seasons</span>
              <h2>Crystal lakes and 768,000 acres of forest.</h2>
              <p>
                In summer the crystal-clear lakes offer fishing, boating and swimming, while the
                White Mountain National Forest opens 768,000 acres of trails and spectacular
                landscapes. Study the craggy stone profiles at Franconia Notch, walk through the
                Flume, or browse the pottery and antique shops.
              </p>
              <p>
                In fall, the foliage is a display of untold beauty — high peaks cupping quaint
                New England villages, each bend in the road another picturesque view.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Seasons: Winter ---------- */}
      <section className="section">
        <div className="container">
          <div className="feature reverse">
            <div className="feature-media reveal d1">
              <img src="./images/exterior/winterpic.jpg" alt="The cabin under fresh snow" />
              <span className="tag">Winter</span>
            </div>
            <div className="feature-text">
              <span className="eyebrow">When the snow falls</span>
              <h2>Pick your winter sport — you'll find it here.</h2>
              <p>
                Winter brings snow and a whole season of recreation. Alpine and cross-country
                skiing, snowmobiling and ice skating are all close by at Waterville Valley, Loon,
                Gunstock, Ragged Mountain and Sunapee.
              </p>
              <p>
                And after a day outdoors, settle onto one of the large porches with a morning
                coffee, take in the sweet sounds of nature, and plan the day ahead.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Season chips ---------- */}
      <section className="section seasons">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">Year-round</span>
            <h2>A retreat for every season.</h2>
          </div>
          <div className="season-grid">
            <article className="season-card"><div className="ic">🍂</div><h3>Autumn</h3><p>Legendary foliage and crisp mountain air across the Lakes Region.</p></article>
            <article className="season-card"><div className="ic">❄️</div><h3>Winter</h3><p>Skiing, snowmobiling and skating minutes from the front door.</p></article>
            <article className="season-card"><div className="ic">🌲</div><h3>Spring</h3><p>Quiet trails, rushing brooks and the forest waking up.</p></article>
            <article className="season-card"><div className="ic">☀️</div><h3>Summer</h3><p>Swim, fish and paddle on Newfound Lake, just one mile away.</p></article>
          </div>
        </div>
      </section>

      {/* ---------- CTA ---------- */}
      <section className="section cta-band">
        <div className="container">
          <h2>Bring your group to Upward Bound.</h2>
          <p>As a nonprofit, we give priority to youth groups, then churches and charitable
            organizations, with families welcomed as space allows. Reach out to check
            available dates.</p>
          <div className="hero-actions">
            <Link to="/contact" className="btn btn--primary">Get in touch <span className="arrow">→</span></Link>
            <Link to="/facilities" className="btn btn--ghost">See the facilities</Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
